import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Users, Search, MessageSquare, Send, X, Plus, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const SalesCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.salesMyCustomers();
      setCustomers(res.data || []);
    } catch {
      toast.error('Kunde inte hämta kunder');
    } finally { setLoading(false); }
  };

  const openCustomer = async (c) => {
    setSelected(c);
    setNotes([]);
    try {
      const r = await api.salesCustomerNotes(c.id);
      setNotes(r.data || []);
    } catch {
      toast.error('Kunde inte hämta anteckningar');
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const r = await api.salesAddCustomerNote(selected.id, newNote.trim());
      setNotes([r.data, ...notes]);
      setNewNote('');
      toast.success('Anteckning sparad');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte spara');
    } finally { setSavingNote(false); }
  };

  const filtered = customers.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.plan_label || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="sales-customers-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users size={22} className="text-[#E8613A]" /> Mina kunder
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {customers.length} kunder skapade av {user?.sales_code || 'dig'}
        </p>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Sök kund…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sales-customers-search" />
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-10">Laddar…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-10 text-center" data-testid="sales-customers-empty">
          <Users size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {search ? 'Inga kunder matchar sökningen' : 'Du har inte skapat några kunder ännu. Gå till "Ny kund" för att skapa din första!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(c => (
            <button
              key={c.id}
              onClick={() => openCustomer(c)}
              className="bg-card border border-border rounded-xl p-4 text-left hover:border-[#E8613A]/40 transition-colors"
              data-testid={`sales-customer-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.plan_label || '—'} · {c.contract_months || '—'} mån</p>
                </div>
                <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                  c.active === false ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                  c.account_type === 'demo' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                  c.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  c.subscription_status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {c.active === false ? 'Pausad' : c.account_type === 'demo' ? 'Demo' : c.subscription_status === 'trialing' ? 'Trial' : c.subscription_status === 'active' ? 'Aktiv' : '—'}
                </span>
                <p className="text-xs font-semibold">{Math.round(c.monthly_price || 0).toLocaleString('sv-SE')} kr/mån</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Customer detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-card border-l border-border overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sales-customer-drawer">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Kund</p>
                <h2 className="text-lg font-bold truncate">{selected.name}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-muted rounded">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3 border-b border-border">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-muted-foreground">Paket</p><p className="font-semibold">{selected.plan_label || '—'}</p></div>
                <div><p className="text-muted-foreground">Bindning</p><p className="font-semibold">{selected.contract_months || '—'} mån</p></div>
                <div><p className="text-muted-foreground">MRR</p><p className="font-semibold">{Math.round(selected.monthly_price || 0).toLocaleString('sv-SE')} kr</p></div>
                <div><p className="text-muted-foreground">Status</p><p className="font-semibold">{selected.subscription_status || '—'}</p></div>
              </div>
            </div>
            <div className="p-5">
              <h3 className="text-sm font-bold flex items-center gap-2 mb-3">
                <MessageSquare size={14} className="text-[#E8613A]" /> Anteckningar ({notes.length})
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Anteckningar är synliga för dig och superadmin.</p>
              <div className="flex gap-2 mb-4">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Skriv en anteckning… (t.ex. 'Kund vill ha utbildning i april')"
                  rows={3}
                  className="flex-1 text-sm rounded-md border border-border bg-background px-3 py-2"
                  data-testid="sales-note-input"
                />
              </div>
              <Button
                onClick={addNote}
                disabled={savingNote || !newNote.trim()}
                className="bg-[#E8613A] hover:bg-[#D4532F] text-white w-full mb-4"
                data-testid="sales-note-save-btn"
              >
                <Send size={12} className="mr-1.5" /> {savingNote ? 'Sparar…' : 'Lägg till anteckning'}
              </Button>
              <div className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-4">Inga anteckningar ännu</p>
                ) : notes.map(n => (
                  <div key={n.id} className="bg-muted/40 border border-border rounded-lg p-3" data-testid={`sales-note-${n.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold">{n.author_name}
                        {n.author_role === 'superadmin' && <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded uppercase">Superadmin</span>}
                        {n.author_sales_code && n.author_role === 'sales' && <span className="ml-1.5 text-[9px] text-muted-foreground font-mono">{n.author_sales_code}</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Calendar size={9} /> {new Date(n.created_at).toLocaleString('sv-SE')}
                      </p>
                    </div>
                    <p className="text-xs text-foreground whitespace-pre-wrap">{n.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCustomers;
